import { FC } from 'react';
import { ResponsivePie } from '@nivo/pie';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import {
    filterZeroPieSlices,
    getBottomLegend,
    getChartFormatting,
    getCircularChartMargin,
    humanizeChartLabel,
} from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { getCenterMetricTextLayout } from '../pieLayout';
import { useNivoReportData } from '../useNivoReportData';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

interface DonutChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const DonutChart: FC<DonutChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();
    const { formatMetricValue, getMetricLabel } = useNivoReportFormatting(component);
    const config = component.configuration as ReportComponentConfig;
    const formatting = getChartFormatting(config);

    const data = [
        {
            id: 'php',
            label: 'php',
            value: 411,
            color: 'hsl(39, 70%, 50%)',
        },
        {
            id: 'stylus',
            label: 'stylus',
            value: 57,
            color: 'hsl(322, 70%, 50%)',
        },
        {
            id: 'ruby',
            label: 'ruby',
            value: 92,
            color: 'hsl(329, 70%, 50%)',
        },
        {
            id: 'css',
            label: 'css',
            value: 465,
            color: 'hsl(245, 70%, 50%)',
        },
        {
            id: 'c',
            label: 'c',
            value: 560,
            color: 'hsl(29, 70%, 50%)',
        },
    ];
    const chartData = filterZeroPieSlices(useNivoReportData(component, data), formatting.showZeroValues);

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsivePie
                fit
                data={chartData}
                margin={getCircularChartMargin(formatting.showLegend)}
                padAngle={2}
                innerRadius={0.875}
                cornerRadius={4}
                activeOuterRadiusOffset={4}
                valueFormat={(value) => formatMetricValue(value)}
                colors={colorsArray}
                theme={nivoChartTheme}
                borderWidth={1}
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 1]],
                }}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor={token.colorBorder}
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={{
                    from: 'color',
                    modifiers: [['darker', 0.2]],
                }}
                tooltip={({ datum }) => (
                    <CustomNivoChartTooltip
                        title={humanizeChartLabel(datum.label ?? datum.id)}
                        entries={[
                            {
                                color: datum.color,
                                label: getMetricLabel() || 'Value',
                                value: datum.value,
                            },
                        ]}
                        formatValue={formatMetricValue}
                    />
                )}
                layers={[
                    'arcs',
                    'legends',
                    (props) => {
                        const total = chartData.reduce((sum, item) => sum + item.value, 0);
                        const formattedTotal = formatMetricValue(total);
                        const metricLayout = getCenterMetricTextLayout(props, formattedTotal);

                        return (
                            <>
                                <text
                                    x={props.centerX}
                                    y={metricLayout.valueY}
                                    textAnchor='middle'
                                    dominantBaseline='central'
                                    {...metricLayout.valueTextFit}
                                    style={{
                                        fontSize: metricLayout.valueFontSize,
                                        fontVariantNumeric: 'tabular-nums',
                                        fontWeight: 600,
                                        fill: token.colorText,
                                    }}
                                >
                                    {formattedTotal}
                                </text>
                                <text
                                    x={props.centerX}
                                    y={metricLayout.labelY}
                                    textAnchor='middle'
                                    dominantBaseline='central'
                                    style={{
                                        fontSize: metricLayout.labelFontSize,
                                        fill: token.colorTextTertiary,
                                    }}
                                >
                                    Total
                                </text>
                            </>
                        );
                    },
                ]}
                legends={formatting.showLegend ? getBottomLegend({ translateY: 56 }) : []}
                // legends={[
                //     {
                //         anchor: 'bottom',
                //         direction: 'row',
                //         justify: false,
                //         translateX: 0,
                //         translateY: 0,
                //         itemsSpacing: 2,
                //         itemDirection: 'left-to-right',
                //         itemWidth: 80,
                //         itemHeight: 20,
                //         itemOpacity: 1,
                //         symbolSize: 12,
                //         symbolShape: 'circle',
                //         toggleSerie: true,
                //         symbolBorderColor: 'rgba(0, 0, 0, .5)',
                //         effects: [
                //             {
                //                 on: 'hover',
                //                 style: {
                //                     itemBackground: 'rgba(0, 0, 0, .03)',
                //                     itemOpacity: 1,
                //                 },
                //             },
                //         ],
                //     },
                // ]}
            />
        </div>
    );
};

export default DonutChart;
